import { FiFileText } from 'react-icons/fi';

export const summaryCompletionData = {
  courseId: 'summary-completion',
  courseType: 'reading-course',
  title: 'Summary Completion',
  icon: FiFileText,
  overview: {
    impact: 'Locate relevant information and select exact words under word-limit rules',
    focus: 'Keyword tracking, grammar fit, and word-limit discipline',
    uses: 'Reading, paraphrase recognition, detail extraction',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What Summary Completion Demands',
      content:
        '<p>You must complete a summary using information from the passage while respecting the exact word limit. This question type tests <strong>location</strong>, <strong>selection</strong>, and <strong>grammar fit</strong>.</p>',
      points: [
        'Read the summary before scanning the passage so you know the topic and the missing information type.',
        'The missing word must fit both the passage and the sentence grammar.',
        'The best answer is usually taken directly from the text, not rewritten.',
      ],
      warning:
        'A correct idea with too many words is still wrong in IELTS.',
    },
    {
      title: 'A Practical Method',
      content: '<p>Use this workflow for each blank:</p>',
      points: [
        'Read the words around the gap and predict what kind of answer is needed: noun, number, adjective, or verb.',
        'Use keywords from the summary to find the matching part of the passage.',
        'Check whether the final answer sounds grammatical in the summary sentence.',
        'Count the words carefully, especially with compounds and numbers.',
      ],
    },
    {
      title: 'What Usually Goes Wrong',
      content: '<p>Most mistakes come from four sources:</p>',
      table: {
        headers: ['Problem', 'Example'],
        rows: [
          ['Wrong section', 'You use a word from a nearby paragraph with a different focus'],
          ['Wrong form', 'The passage has "decide" but the summary needs a noun like "decision"'],
          ['Word-limit error', 'You write three words when the instruction allows two'],
          ['Meaning drift', 'You choose a word that fits grammar but changes the passage meaning'],
        ],
      },
    },
    {
      title: 'Grammar Fit Matters',
      content: '<p>The answer must fit the sentence structure, not just the topic.</p>',
      examples: [
        {
          label: 'Passage wording',
          text: '"The committee reached a decision after two weeks of debate."',
        },
        {
          label: 'Summary gap',
          text: '"After two weeks of debate, the committee made a ___."',
        },
        {
          label: 'Best answer',
          text: 'decision',
        },
      ],
    }
  ],
  exercises: [
    {
      title: 'Choose the Best Gap Answer',
      type: 'select',
      instructions: 'Select the option that best completes the summary using the passage.',
      questions: [
        {
          text:
            'Passage: "The study found that sleep quality improved when participants reduced screen use after 9 p.m."\nSummary: Sleep quality improved when participants cut down on ___ after 9 p.m.',
          options: [
            'screen use',
            'electricity bills',
            'morning exercise'
          ],
          answer: 'screen use',
          explanation:
            'The answer directly matches the wording and fits the summary sentence.',
        },
        {
          text:
            'Passage: "By 2018, the harbour handled twice as much cargo as it had in 2005."\nSummary: In 2018, the harbour processed ___ the cargo recorded in 2005.',
          options: ['twice as much', 'slightly less', 'all of the'],
          answer: 'twice as much',
          explanation:
            'This is the precise phrase used in the passage and fits grammatically.',
        },
        {
          text:
            'Passage: "The exhibition was delayed because several paintings required additional conservation work."\nSummary: The exhibition delay was caused by extra ___ work.',
          options: ['conservation', 'transport', 'security'],
          answer: 'conservation',
          explanation:
            'The passage explicitly states that additional conservation work caused the delay.',
        },
      ],
    },
    {
      title: 'Word-Limit Awareness',
      type: 'select',
      instructions: 'Choose the answer that obeys the instruction.',
      questions: [
        {
          text:
            'Instruction: NO MORE THAN TWO WORDS.\nPassage: "The main cause was a shortage of trained staff."\nSummary: The main cause was a ___ of trained staff.',
          options: ['shortage', 'shortage problem', 'lack of available trained staff'],
          answer: 'shortage',
          explanation:
            'Only "shortage" fits naturally and respects the two-word maximum.',
        },
        {
          text:
            'Instruction: ONE WORD ONLY.\nPassage: "Residents complained about excessive noise from construction vehicles."\nSummary: Residents complained about construction ___ .',
          options: ['vehicles', 'heavy vehicles', 'vehicle traffic'],
          answer: 'vehicles',
          explanation:
            'The instruction allows one word only, so the longer options are automatically wrong.',
        },
      ],
    },
    {
      title: 'Type the Missing Word',
      type: 'fill-in',
      instructions: 'Type the exact word or short phrase from the passage.',
      questions: [
        {
          text:
            'Passage: "The lecture series was aimed primarily at first-year engineering students."\nSummary: The lecture series mainly targeted first-year ___ students.',
          answer: 'engineering',
          explanation:
            'The summary needs the field of study before "students".',
        },
        {
          text:
            'Passage: "Farmers adopted drip irrigation to reduce water waste during dry months."\nSummary: Drip irrigation was adopted to reduce water ___ .',
          answer: 'waste',
          explanation:
            'The key noun in the passage completes the summary exactly.',
        },
        {
          text:
            'Passage: "After the software update, customer complaints fell sharply."\nSummary: Customer complaints dropped sharply after the software ___ .',
          answer: 'update',
          explanation:
            'The passage uses the noun "update", which fits the summary directly.',
        },
      ],
    },
    {
      title: 'Explain Your Choice',
      type: 'writing',
      instructions: 'Explain why your answer fits both meaning and grammar.',
      questions: [
        {
          text:
            'Passage: "The river became safer for fish after factories reduced chemical discharge."\nSummary: The river became safer for fish when factories reduced chemical ___ .\nWhy is your answer correct?',
          example:
            'The correct answer is "discharge" because it is the exact noun used in the passage and it fits naturally after "chemical" in the summary.',
          tip:
            'A strong explanation mentions both passage evidence and sentence fit.',
        },
        {
          text:
            'Passage: "The company introduced flexible schedules to improve staff retention."\nSummary: Flexible schedules were introduced to improve staff ___ .\nWhy is your answer correct?',
          example:
            'The answer is "retention" because that is the exact goal stated in the passage, and it completes the noun phrase "staff retention" correctly.',
          tip:
            'Look for the exact collocation used in the text.',
        },
      ],
    },
  ],
  tips: [
    'Predict the missing word type before you look back at the passage.',
    'Use nearby words in the summary to locate the correct section faster.',
    'Always re-read the completed sentence aloud in your head to test grammar.',
    'Underline the word-limit instruction before starting.',
  ],
  dailyPractice: [
    'Take a paragraph from an article and write a 2-sentence summary with 3 gaps.',
    'Practice copying exact words from a passage without changing form.',
    'Review old answers and identify whether the mistake was location, grammar, or word limit.',
    'Do one timed summary-completion set focusing only on gap prediction.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Summary Completion: finding the right section of the passage, choosing exact words, and checking grammar plus word-limit accuracy.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...summaryCompletionData,
      title: summaryCompletionData.title + ' (Foundations)',
      overview: { ...summaryCompletionData.overview, duration: '15-20 minutes' },
      sections: summaryCompletionData.sections.slice(0, 2),
      exercises: [
        {
          ...summaryCompletionData.exercises[0],
          title: 'Choose the Best Gap Answer (Basic)',
          questions: summaryCompletionData.exercises[0].questions.slice(0, 2),
        },
        {
          ...summaryCompletionData.exercises[2],
          title: 'Type the Missing Word (Basic)',
          questions: summaryCompletionData.exercises[2].questions.slice(0, 2),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...summaryCompletionData,
      title: summaryCompletionData.title + ' (Advanced)',
      overview: { ...summaryCompletionData.overview, duration: '30-35 minutes' },
      sections: [
        ...summaryCompletionData.sections,
        {
          title: 'Advanced Summary Traps',
          content:
            '<p>At higher levels, IELTS often places the answer beside a competing phrase that looks grammatical but changes the meaning. Your job is to choose the phrase that preserves the writer\'s exact claim.</p>',
          points: [
            'Watch for near-synonyms that alter strength or certainty.',
            'Do not replace the passage wording if the instruction requires direct transfer.',
            'Check whether the answer belongs to the right clause of the sentence.',
          ],
        },
      ],
      exercises: [
        ...summaryCompletionData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Advanced Precision Drill',
          type: 'select',
          instructions: 'Choose the option that preserves the passage meaning most exactly.',
          questions: [
            {
              text:
                'Passage: "The policy generated modest gains in productivity, but only after a long adjustment period."\nSummary: Productivity gains appeared only after a long ___ period.',
              options: ['adjustment', 'productive', 'policy'],
              answer: 'adjustment',
              explanation:
                'Only "adjustment" preserves the original meaning and grammar.',
            },
          ],
        },
      ],
      tips: [
        ...(summaryCompletionData.tips || []),
        'At Band 8+, compare candidate answers for both precision and register.',
        'When two answers seem possible, choose the one closest to the passage wording.',
      ],
    };
  }
  return summaryCompletionData;
}
